import { IHeavyDutyMachinery } from '../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { isBlankFormValue } from './machineryDraftHelper';

/**
 * The two things that make publishing the SECOND machine quick.
 *
 * Neither has anything to do with the assistant, and that is the point: what
 * a host repeats between one listing and the next is not technical data a
 * model can look up, it is their own yard, their own delivery terms and their
 * own rules -- which are sitting in their last listing, for free and with no
 * chance of being invented. And most of them own the same machine several
 * times over.
 */

/**
 * Which listing to copy from: the one they published last.
 *
 * Last rather than "the most similar", deliberately. A host's terms change
 * over time and the newest listing is the closest thing to what they believe
 * today; picking by category instead would happily resurrect the delivery
 * radius they abandoned six months ago because one old mini-digger still
 * carries it.
 *
 * Status is ignored on purpose -- a suspended or parked machine still carries
 * the same yard and the same policy, and those are what is being copied.
 */
export const mostRecentListing = (listings: IHeavyDutyMachinery[]): IHeavyDutyMachinery | undefined =>
  listings
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

export interface InheritedSetup {
  values: Record<string, unknown>;
  /** Exactly what was copied, so undoing it can put back only that and nothing else. */
  filledFields: string[];
}

/**
 * The host's own previous listing, minus everything that is about THIS
 * machine rather than about how they work.
 *
 * **What is deliberately NOT here is the whole design.** No price, and no
 * deposit. Both are money, both scale with what the particular machine is
 * worth, and both go wrong silently: a copied price is noticed the first time
 * somebody books, and a copied deposit is noticed the first time something
 * breaks, which is far too late. Everything that IS copied -- the yard, the
 * delivery terms, the booking rules, the policy -- is the same for every
 * machine a host owns, and is visible on the screen it lands on.
 *
 * Serial number, internal reference, engine hours and year are not copied
 * either, for the opposite reason: they identify one physical unit.
 *
 * @param current the form store as it stands (getFieldsValue(true)).
 * @param defaults the wizard's own initial values -- a field still holding
 *        one of those has not been answered by anybody, so the host's own
 *        previous choice is strictly better than our generic guess.
 */
export const inheritFromListing = (
  previous: IHeavyDutyMachinery,
  current: Record<string, unknown>,
  defaults: Record<string, unknown>
): InheritedSetup => {
  const values: Record<string, unknown> = {};
  const filledFields: string[] = [];

  const offer = (field: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;

    const held = current[field];
    const stillOurDefault = Object.prototype.hasOwnProperty.call(defaults, field) && held === defaults[field];
    if (!isBlankFormValue(held) && !stillOurDefault) return;

    // Nothing was actually changed, so nothing is claimed. Keeps the count in
    // the banner honest when their last listing simply agreed with us.
    if (held === value) return;

    values[field] = value;
    filledFields.push(field);
  };

  // Where the yard is. The single most tedious block to retype, and the one
  // that is most reliably identical -- and it lands on the wizard's first
  // step, with the map right next to it.
  offer('city', previous.city);
  offer('province', previous.province);
  offer('region', previous.region);
  offer('formattedAddress', previous.formattedAddress);
  offer('administrativeLevel', previous.administrativeLevel);
  offer('country', previous.country);
  offer('countryCode', previous.countryCode);
  offer('latitude', previous.latitude);
  offer('longitude', previous.longitude);
  offer('privateAddress', previous.privateAddress);
  offer('hostCurrencyCode', previous.hostCurrencyCode);

  // How they rent things out.
  const hireModes = [
    previous.dryHireAvailable ? 'DryHireAvailable' : undefined,
    previous.wetHireHostOperatorAvailable ? 'WetHireHostOperatorAvailable' : undefined,
    previous.wetHireThirdPartyAvailable ? 'WetHireThirdPartyAvailable' : undefined,
    previous.deliveryAvailable ? 'DeliveryAvailable' : undefined,
  ].filter(Boolean) as string[];
  if (hireModes.length > 0 && isBlankFormValue(current.hireModesList)) {
    values.hireModesList = hireModes;
    filledFields.push('hireModesList');
  }

  offer('rentalMode', previous.rentalMode);
  offer('deferredBookingEnabled', previous.deferredBookingEnabled);
  offer('maxOperators', previous.maxOperators);

  // Transport. Rates per km and the dispatch fee ARE money, but unlike a
  // rental price they are a property of the lorry and the yard rather than of
  // the machine on it -- the same host charges the same to send the same
  // truck the same distance.
  offer('pricePerKmDelivery', previous.pricePerKmDelivery);
  offer('fixedDispatchFee', previous.fixedDispatchFee);
  offer('maxDeliveryRadiusKm', previous.maxDeliveryRadiusKm);
  offer('minRentalDaysForDelivery', previous.minRentalDaysForDelivery);
  offer('restrictDeliveryToSameCountry', previous.restrictDeliveryToSameCountry);
  offer('chargeStaffTimeForDelivery', previous.chargeStaffTimeForDelivery);
  offer('deliveryVehicleId', previous.deliveryVehicleId);

  // How somebody gets to it, and the rules around booking it.
  offer('accessType', previous.accessType);
  offer('parking', previous.parking);
  offer('bufferMinutes', previous.bufferMinutes);
  offer('minAdvanceBookingHours', previous.minAdvanceBookingHours);
  offer('maxAdvanceBookingDays', previous.maxAdvanceBookingDays);
  offer('minBookingDurationMinutes', previous.minBookingDurationMinutes);
  offer('maxBookingDurationMinutes', previous.maxBookingDurationMinutes);
  offer('slotGranularityMinutes', previous.slotGranularityMinutes);
  offer('cancellationPolicy', previous.cancellationPolicy);
  offer('timezone', previous.timezone);
  offer('welcomeMessage', previous.welcomeMessage);

  return { values, filledFields };
};

/**
 * Putting back what was copied, and only that.
 *
 * A field with a wizard default goes back to the default rather than to
 * nothing: emptying `cancellationPolicy` would leave the host with a required
 * field they never touched and no idea why.
 */
export const revertInherited = (
  filledFields: string[],
  defaults: Record<string, unknown>
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};
  filledFields.forEach((field) => {
    values[field] = Object.prototype.hasOwnProperty.call(defaults, field) ? defaults[field] : undefined;
  });
  return values;
};

/** More than this in one go is not a fleet, it is a mistake with the stepper. */
export const MAX_UNITS = 10;

/** The three fields that belong to one physical machine rather than to a model. */
export interface UnitIdentity {
  serialNumber?: string;
  internalReference?: string;
  engineHoursAtListing?: number;
}

/**
 * What tells one of several identical units apart.
 *
 * A host with three of the same excavator gets three listings, because a
 * listing is the unit of everything else here -- its own calendar, its own
 * slot claims, its own telemetry, its own check-in. A quantity column would
 * have to be understood by every one of those.
 *
 * The first unit is exactly what they filled in. The rest deliberately arrive
 * WITHOUT a chassis number and without engine hours: those identify one
 * machine, and copying them across three listings is worse than leaving them
 * empty, because a wrong chassis number looks like a right one. They are told
 * apart by internalReference, which is the host's own private code and is
 * precisely what that field is for -- asking for three real chassis numbers
 * up front would hand back with one hand the typing this saved with the other.
 *
 * @param index 0-based.
 * @param unitLabel the word for "unit" in the host's own language.
 */
export const unitIdentityFor = (
  index: number,
  count: number,
  typed: UnitIdentity,
  unitLabel: string
): UnitIdentity => {
  if (count <= 1) return typed;

  const suffix = `${index + 1}`;
  const reference = typed.internalReference ? `${typed.internalReference} ${suffix}` : `${unitLabel} ${suffix}`;

  if (index === 0) return { ...typed, internalReference: reference };

  return { serialNumber: undefined, engineHoursAtListing: undefined, internalReference: reference };
};
